/**
 * Which hue an exercise TYPE wears, as two custom properties a stylesheet reads.
 *
 * The same shape and the same reason as `planCategoryColour` beside it: the mapping is a display
 * decision, the token layer owns the values, and a component reads `var(--type-ink)` without ever
 * naming a ramp. The caller puts the returned object on `style` and the stylesheet does the rest.
 *
 * ── Six of the seven are the PLAN CATEGORY palette, and that is deliberate ──────────────────
 * A plan's category and an exercise's type are two taxonomies, but they name the same five kinds of
 * training and the KANON specimen draws them in the same five hues — so a Siłowe movement and a
 * Siła plan are one colour across the app. Pairing them here rather than minting a second palette
 * is what keeps that true when a hue is retuned.
 *
 * ── The seventh and the eighth ──────────────────────────────────────────────────────────────
 * `plyometric` has no category to borrow and reads `--ex-plyometric`, the one token in
 * `tokens/colors.css` that did not come off a handoff — the phone owns that hue as data. `custom`
 * is not a kind of training at all; it is "the trainer did not say", so it reads the neutral
 * `--cat-hybrid`, which is what `null` answers with too.
 */

import type { CSSProperties } from 'react';

import type { ExerciseTypeKey } from '../model/exerciseType';

/**
 * The taxonomy, read from the one module that owns it.
 *
 * It was spelled out here while this file lived in `shared/ui`, where a slice may not import from
 * `entities/`. It lives in this slice now (`CLAUDE.md`: promote on the SECOND consumer), so the
 * copy has no reason to exist and the alias is the real union — widening the taxonomy without
 * teaching this table is a type error here rather than a silently neutral glyph.
 */
export type ExerciseTypeColourKey = ExerciseTypeKey;

const INK: Record<ExerciseTypeColourKey, string> = {
  strength: 'var(--cat-strength)',
  bodyweight: 'var(--cat-bodyweight)',
  cardio: 'var(--cat-cardio)',
  mobility: 'var(--cat-flexibility)',
  conditioning: 'var(--cat-hiit)',
  plyometric: 'var(--ex-plyometric)',
  custom: 'var(--cat-hybrid)',
};

const WASH: Record<ExerciseTypeColourKey, string> = {
  strength: 'var(--cat-strength-wash)',
  bodyweight: 'var(--cat-bodyweight-wash)',
  cardio: 'var(--cat-cardio-wash)',
  mobility: 'var(--cat-flexibility-wash)',
  conditioning: 'var(--cat-hiit-wash)',
  plyometric: 'var(--ex-plyometric-wash)',
  custom: 'var(--cat-hybrid-wash)',
};

/**
 * `null` is a type this build has never heard of, and it reads as the neutral rather than as a
 * guess — the same answer `custom` gets, because "the backend said something I do not know" and
 * "the trainer did not say" are both the absence of a kind of training.
 */
export function exerciseTypeColour(key: ExerciseTypeColourKey | null): CSSProperties {
  const resolved = key ?? 'custom';
  return {
    '--type-ink': INK[resolved],
    '--type-wash': WASH[resolved],
  } as CSSProperties;
}
